import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Save } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { associationApi } from "@/services/api";
import { sharedProjectDetailFields } from "@/forms/lender/projectDetails.mjs";
import type { FlatField } from "@/forms/fannie-mae-1076/spec.mjs";

/**
 * Project Details: the lender questionnaire answered once for the whole
 * association.
 *
 * Fannie Mae 1076 asks some 150 questions about the project, and the answers
 * are the same for every unit — "in an association of 900 units, making them
 * fill this in again and again is a problem". So they are answered here, once,
 * and every order's Process screen starts from these values and adjusts what
 * changed (delinquencies, units sold).
 *
 * The questions come straight from the spec, grouped by the sections of the
 * official form, so this tab, the form and the PDF never disagree about what
 * exists. Values are strings; a checkbox is "Yes" or "No"; a question never
 * answered has no key at all.
 *
 * Saves on its own, separately from the association form around it: it is a
 * different table with a different rhythm of change.
 */

interface ProjectDetailsTabProps {
  associationId: number;
  associationName?: string;
}

/** The Select component cannot carry an empty value; this stands in for it. */
const BLANK = "__blank__";

/** Questions whose answers run long enough to want a textarea. */
const LONG_ANSWER = /(detail|terms|purpose|comments|explanation|remaining|when|name_if_different)$/;

interface SectionGroup {
  key: string;
  step: string;
  title: string;
  fields: FlatField[];
}

function groupBySection(fields: FlatField[]): SectionGroup[] {
  const groups: SectionGroup[] = [];
  for (const field of fields) {
    const key = `${field.stepIndex}-${field.sectionIndex}`;
    let group = groups[groups.length - 1];
    if (!group || group.key !== key) {
      group = { key, step: field.stepTitle, title: field.sectionTitle, fields: [] };
      groups.push(group);
    }
    group.fields.push(field);
  }
  return groups;
}

export const ProjectDetailsTab: React.FC<ProjectDetailsTabProps> = ({
  associationId,
  associationName,
}) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const sections = useMemo(() => groupBySection(sharedProjectDetailFields()), []);
  const totalQuestions = useMemo(
    () => sections.reduce((n, s) => n + s.fields.length, 0),
    [sections],
  );

  const {
    data: details,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["association-project-details", associationId],
    queryFn: () => associationApi.getProjectDetails(associationId),
    enabled: !!associationId,
  });

  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (details) {
      setAnswers(details.answers ?? {});
      setDirty(false);
    }
  }, [details]);

  const saveMutation = useMutation({
    mutationFn: (next: Record<string, string>) =>
      associationApi.updateProjectDetails(associationId, next),
    onSuccess: (saved) => {
      queryClient.setQueryData(["association-project-details", associationId], saved);
      setDirty(false);
      toast({
        title: "Project Details saved",
        description: `${Object.keys(saved.answers ?? {}).length} of ${totalQuestions} questions answered. New orders for this association will start from these.`,
      });
    },
    onError: (err: Error) => {
      toast({
        variant: "destructive",
        title: "Could not save Project Details",
        description: err.message,
      });
    },
  });

  const setAnswer = (name: string, value: string) => {
    setAnswers((prev) => {
      const next = { ...prev };
      if (value === "") delete next[name];
      else next[name] = value;
      return next;
    });
    setDirty(true);
  };

  const answeredIn = (fields: FlatField[]) =>
    fields.filter((f) => (answers[f.name] ?? "") !== "").length;

  const renderControl = (field: FlatField) => {
    const value = answers[field.name] ?? "";
    const id = `pd-${field.name}`;

    switch (field.type) {
      case "checkbox":
        return (
          <div className="flex items-start gap-3">
            <Checkbox
              id={id}
              checked={value === "Yes"}
              onCheckedChange={(checked) => setAnswer(field.name, checked ? "Yes" : "No")}
              className="mt-1"
            />
            <Label htmlFor={id} className="font-normal leading-snug cursor-pointer">
              {field.label}
            </Label>
          </div>
        );

      case "select":
        return (
          <div className="space-y-1.5">
            <Label htmlFor={id}>{field.label}</Label>
            <Select
              value={value === "" ? BLANK : value}
              onValueChange={(v) => setAnswer(field.name, v === BLANK ? "" : v)}
            >
              <SelectTrigger id={id} className="w-full md:w-96">
                <SelectValue placeholder="Not answered" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={BLANK}>
                  <span className="text-muted-foreground">Not answered</span>
                </SelectItem>
                {(field.options ?? []).map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case "date":
        return (
          <div className="space-y-1.5">
            <Label htmlFor={id}>{field.label}</Label>
            <Input
              id={id}
              type="date"
              value={value}
              onChange={(e) => setAnswer(field.name, e.target.value)}
              className="w-full md:w-60"
            />
          </div>
        );

      default:
        return (
          <div className="space-y-1.5">
            <Label htmlFor={id}>{field.label}</Label>
            {LONG_ANSWER.test(field.name) ? (
              <Textarea
                id={id}
                value={value}
                onChange={(e) => setAnswer(field.name, e.target.value)}
                className="min-h-20"
              />
            ) : (
              <Input
                id={id}
                value={value}
                onChange={(e) => setAnswer(field.name, e.target.value)}
              />
            )}
          </div>
        );
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
        Loading Project Details…
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8 text-destructive">
          Could not load Project Details: {(error as Error).message}
        </CardContent>
      </Card>
    );
  }

  const answered = Object.keys(answers).length;

  return (
    <Card
      // Enter inside one of these inputs must not submit the association form
      // that wraps every tab of this page.
      onKeyDown={(e) => {
        if (e.key === "Enter" && (e.target as HTMLElement).tagName === "INPUT") {
          e.preventDefault();
        }
      }}
    >
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <CardTitle>Project Details</CardTitle>
            <CardDescription className="mt-1 max-w-2xl">
              Everything lenders ask about the project, answered once for{" "}
              {associationName ? <strong>{associationName}</strong> : "this association"}.
              Every order starts from these answers; only what changed is adjusted when
              the document is processed.
            </CardDescription>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground whitespace-nowrap">
              {answered} of {totalQuestions} answered
              {details?.updated_at && (
                <> · saved {new Date(details.updated_at).toLocaleDateString()}</>
              )}
            </span>
            <Button
              type="button"
              onClick={() => saveMutation.mutate(answers)}
              disabled={!dirty || saveMutation.isPending}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {saveMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Project Details
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Accordion type="multiple" className="w-full">
          {sections.map((section) => (
            <AccordionItem key={section.key} value={section.key}>
              <AccordionTrigger className="hover:no-underline">
                <div className="flex flex-1 items-center justify-between gap-4 pr-4 text-left">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">
                      {section.step}
                    </div>
                    <div className="font-medium">{section.title}</div>
                  </div>
                  <span className="text-sm text-muted-foreground tabular-nums whitespace-nowrap">
                    {answeredIn(section.fields)} / {section.fields.length}
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="grid gap-5 pt-2 pb-2 md:grid-cols-2">
                  {section.fields.map((field) => (
                    <div
                      key={field.name}
                      className={
                        field.type === "text" && LONG_ANSWER.test(field.name)
                          ? "md:col-span-2"
                          : undefined
                      }
                    >
                      {renderControl(field)}
                      {field.help && (
                        <p className="mt-1 text-xs text-muted-foreground">{field.help}</p>
                      )}
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        {/* The Insurance section sits at the bottom of 180 questions; the
            button within reach there must be THIS save, not the page's. */}
        <div className="mt-6 flex justify-end">
          <Button
            type="button"
            onClick={() => saveMutation.mutate(answers)}
            disabled={!dirty || saveMutation.isPending}
            className="bg-orange-600 hover:bg-orange-700"
          >
            {saveMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save Project Details
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProjectDetailsTab;
